import { CircularProgress, Grid, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Cell, Pie, PieChart } from 'recharts';
import StoryContext from '../../contexts/StoryContext';
import { rdsSelect } from '../../modules/rds';
import states from '../../modules/states';
import { toPercent } from '../../modules/text';
import MessageBox from '../MessageBox';
import NoDataVisual from '../NoDataVisual';
import ResponsiveContainer from '../ResponsiveContainer';
import { years } from '../../modules/years';

function PercentOfNSHRequests() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [noData, setNoData] = useState(false);
  const [error, setError] = useState(null);
  const [pieData, setPieData] = useState([]);
  const [percentValue, setPercentValue] = useState(25);
  const { primaryColor, contrastColor } = useContext(StoryContext);

  useEffect(() => {
    function getRequests() {
      const whereClause = `submission_state=${stateCode.toUpperCase()}`;

      return rdsSelect('ccdc_qst', 'qst_state_referrals', {
        cols: 'referral_requests_total,nontraditional_hours_requests',
        where: whereClause,
        format: 'amcharts',
      });
    }

    setReady(false);
    setError(null);
    Promise.all([getRequests()])
      .then((responses) => {
        if (responses[0].parsedBody.dataProvider.length > 0) {
          const d = responses[0].parsedBody.dataProvider[0];
          if (!d.nontraditional_hours_requests || !d.referral_requests_total) {
            setNoData(true); // no data to display
          } else {
            setPercentValue(d.nontraditional_hours_requests / d.referral_requests_total);
            setPieData([{ name: 'Child Care', value: (d.nontraditional_hours_requests / d.referral_requests_total) * 100 }, { name: 'Other', value: 100 - ((d.nontraditional_hours_requests / d.referral_requests_total) * 100) }]);
          }
        } else {
          setError(new Error('No data returned'));
        }
      })
      .catch((rdsError) => {
        // console.error(rdsError);
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [stateCode]);

  const renderCustomizedLabel = ({
    cx, cy, percent, index,
  }) => {
    if (index > 0) return null; // only need to show the first value in the center of the donut

    return (
      <text x={cx} y={cy} fontSize="3em" fill={primaryColor} textAnchor="middle" dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (!ready) {
    return <CircularProgress />;
  }

  if (noData) {
    return (
      <Grid container spacing={2} justify="center">
        <Grid item xs={12} md={8}>
          {error && <Typography variant="body1" align="center" color="error">{error.message}</Typography>}
          <NoDataVisual message="NSH care referral data not reported." />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} justify="center">
      <Grid item xs={12} md={8}>
        {error && <Typography variant="body1" align="center" color="error">{error.message}</Typography>}
        <MessageBox>
          <Typography variant="body1" align="left" gutterBottom>
            In <b>{years.inReview}</b>, <b>{toPercent(percentValue)}</b> of families in <b>{states[stateCode.toUpperCase()]}</b> requested NSH child care from their CCR&amp;R.<sup>*</sup>
          </Typography>
          <Typography variant="body2"><sup>*</sup> phone referral data.</Typography>
        </MessageBox>
      </Grid>

      <Grid item xs={12}>
        <ResponsiveContainer height={270}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              startAngle={0}
              innerRadius={80}
              outerRadius={110}
              label={renderCustomizedLabel}
              labelLine={false}
              fill={primaryColor}
            >
              {pieData.map((entry, index) => (
                <Cell key={entry} fill={[primaryColor, contrastColor][index]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Grid>

    </Grid>
  );
}

export default PercentOfNSHRequests;
